import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  removeFromCartBackend,
  fetchCartFromBackend,
  clearCartBackend,
  updateCartItemBackend,
} from "../redux/cart/CartSlice";
// Import salon cart actions
import {
  removeFromSalonHomeCartBackend,
  fetchSalonHomeCartFromBackend,
  clearSalonHomeCartBackend,
  updateSalonHomeCartItemBackend,
} from "../redux/cart/HomeSalon";
import {
  FaTrash,
  FaClock,
  FaCheck,
  FaStore,
  FaShoppingBag,
  FaChevronLeft,
  FaChevronDown,
  FaArrowRight,
  FaTimes,
  FaShoppingCart,
  FaPlus,
  FaMinus,
} from "react-icons/fa";

import { FaCircleCheck, FaScissors } from "react-icons/fa6";

const ShopCart = () => {
  const { CartItems, netPayable, cartTotalMrp, loading } = useSelector(
    (state) => state.Cart
  );
  // Salon cart state
  const {
    salonHomeCartItems,
    salonHomeNetPayable,
    salonHomeCartTotalMrp,
    loading: salonLoading,
  } = useSelector((state) => state.HomeSalonCart);
  
  const { currentAddress, isAuth, clientid } = useSelector(
    (state) => state.Authentication
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State for dropdown and authentication
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSalonItem, setIsSalonItem] = useState(false); // Track if selected item is from salon cart
  const [quantities, setQuantities] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("shop"); // 'shop' or 'salon'

  // Generate quantities from 1 to 10
  useEffect(() => {
    const qtyArray = Array.from({ length: 10 }, (_, i) => i + 1);
    setQuantities(qtyArray);
  }, []);

  useEffect(() => {
    const authStatus = isAuth && !!clientid;
    setIsAuthenticated(authStatus);

    if (authStatus) {
      console.log("🛒 Fetching carts for authenticated user:", clientid);
      dispatch(fetchCartFromBackend());
      dispatch(fetchSalonHomeCartFromBackend());
    } else {
      console.log("🛒 No authenticated user, skipping cart fetch");
    }
  }, [dispatch, isAuth, clientid]);

  // Filter items based on active tab
  const shopItems = CartItems.filter(
    (item) => item.for === "Shop" || !item.for
  );
  const salonItems = salonHomeCartItems; // Use salonHomeCartItems directly

  const currentItems = activeTab === "shop" ? shopItems : salonItems;
  const currentNetPayable = activeTab === "shop" 
    ? netPayable 
    : salonHomeNetPayable;
  const currentCartTotalMrp = activeTab === "shop" 
    ? cartTotalMrp 
    : salonHomeCartTotalMrp;
  const savings = currentCartTotalMrp - currentNetPayable;
  
  const currentLoading = activeTab === "shop" ? loading : salonLoading;

  const deleteServiceClick = async (item) => {
    if (!isAuthenticated) {
      alert("Authentication Required\nPlease login to manage your cart");
      navigate("/login");
      return;
    }

    try {
      if (activeTab === "shop") {
        await dispatch(removeFromCartBackend(item.ProductId)).unwrap();
      } else {
        await dispatch(removeFromSalonHomeCartBackend(item.ProductId)).unwrap();
      }
      alert("Success\nItem removed from cart");
    } catch (error) {
      console.error("Failed to remove item:", error);
      if (error?.includes?.("No authenticated user")) {
        alert("Authentication Required\nPlease login to manage your cart");
        navigate("/login");
      } else {
        alert("Error\nFailed to remove item from cart");
      }
    }
  };

  const handleClearCart = async () => {
    if (!isAuthenticated) {
      alert("Authentication Required\nPlease login to manage your cart");
      navigate("/login");
      return;
    }

    try {
      if (activeTab === "shop") {
        // For shop cart, remove each item individually
        const itemsToClear = currentItems.map((item) => item.ProductId);
        for (const productId of itemsToClear) {
          await dispatch(removeFromCartBackend(productId)).unwrap();
        }
      } else {
        // For salon cart, use clear cart action
        await dispatch(clearSalonHomeCartBackend()).unwrap();
      }
      alert("Success\nCart cleared successfully");
    } catch (error) {
      console.error("Failed to clear cart:", error);
      if (error?.includes?.("No authenticated user")) {
        alert("Authentication Required\nPlease login to manage your cart");
        navigate("/login");
      } else {
        alert("Error\nFailed to clear cart");
      }
    }
  };

  const handleScheduleAddress = () => {
    if (!isAuthenticated) {
      alert("Authentication Required\nPlease login to proceed");
      navigate("/login");
      return;
    }

    // Navigate to schedule address selection
    navigate("/slot-booking", {
      state: {
        from: activeTab === "shop" ? "shopcart" : "saloncart",
        cartItems: currentItems,
        netPayable: currentNetPayable,
        cartTotalMrp: currentCartTotalMrp,
        activeTab: activeTab,
      },
    });
  };

  const showQuantityDropdown = (item) => {
    if (!isAuthenticated) {
      alert("Authentication Required\nPlease login to update cart");
      navigate("/login");
      return;
    }

    setSelectedItem(item);
    setIsSalonItem(activeTab === "salon");
    setDropdownVisible(true);
  };

  const updateQuantity = async (newQuantity) => {
    if (!selectedItem || !isAuthenticated) return;

    try {
      if (isSalonItem) {
        await dispatch(
          updateSalonHomeCartItemBackend({
            productId: selectedItem.ProductId,
            quantity: newQuantity,
          })
        ).unwrap();
      } else {
        await dispatch(
          updateCartItemBackend({
            productId: selectedItem.ProductId,
            quantity: newQuantity,
          })
        ).unwrap();
      }

      setDropdownVisible(false);
      setSelectedItem(null);
      setIsSalonItem(false);
      
      // Refresh the appropriate cart
      if (isSalonItem) {
        dispatch(fetchSalonHomeCartFromBackend());
      } else {
        dispatch(fetchCartFromBackend());
      }
    } catch (error) {
      console.error("Failed to update quantity:", error);
      if (error?.includes?.("No authenticated user")) {
        alert("Authentication Required\nPlease login to update cart");
        navigate("/login");
      } else {
        alert("Error\nFailed to update quantity");
      }
    }
  };

  const CartItemCard = ({ item }) => (
    <div className="p-3 rounded-3 mb-3" style={{ background: "#fff" }}>
      {/* Header Section - Store Name / Shop Product */}
      {item.SalonName ? (
        <div className="d-flex align-items-center mb-3 py-1 px-2 bg-light rounded-pill">
          <FaStore className="text-primary me-2 small" />
          <small className="fw-semibold text-primary">{item.SalonName}</small>
        </div>
      ) : activeTab === "shop" ? (
        <div className="d-flex align-items-center mb-3 py-1 px-2 bg-warning bg-opacity-10 rounded-pill">
          <FaShoppingCart className="text-warning me-2 small" />
          <small className="fw-semibold text-warning">Shop Product</small>
        </div>
      ) : (
        <div className="d-flex align-items-center mb-3 py-1 px-2 bg-info bg-opacity-10 rounded-pill">
          <FaScissors className="text-info me-2 small" />
          <small className="fw-semibold text-info">Salon Service</small>
        </div>
      )}

      <div className="d-flex align-items-start">
        {/* Product Image */}
        <div className="flex-shrink-0 me-3">
          <img
            src={item.ImgUrl}
            alt={item.ProductName}
            className="rounded-3"
            style={{
              width: "90px",
              height: "90px",
              objectFit: "cover",
            }}
          />
        </div>

        {/* Product Details */}
        <div className="flex-grow-1 text-start">
          <h6 className="fw-bold text-dark mb-1">{item.ProductName}</h6>

          {/* Price Section */}
          <div className="d-flex align-items-center mb-2 flex-wrap">
            <h6 className="fw-bold text-dark mb-0 me-2">₹{item.Price}</h6>
            <small className="text-muted text-decoration-line-through me-2">
              ₹{item.Mrp}
            </small>
            <span className="badge bg-success bg-opacity-10 text-success small px-2 py-1">
              {(((item.Mrp - item.Price) / item.Mrp) * 100).toFixed(0)}% OFF
            </span>
          </div>

          {/* Duration */}
          {item.Duration && (
            <div className="d-flex align-items-center text-muted mb-2 small">
              <FaClock className="me-1" />
              {item.Duration}
            </div>
          )}

          {/* Services */}
          <div className="mb-2">
            {item.Services?.slice(0, 2).map((service, index) => (
              <div key={index} className="d-flex align-items-start small mb-1">
                <FaCircleCheck className="text-success me-2 mt-1 small" />
                <span className="text-muted">
                  {service.service && service.service.length > 50
                    ? `${service.service.slice(0, 50)}...`
                    : service.service || service}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Actions & Total */}
        <div
          className="text-end d-flex flex-column justify-content-between"
          style={{ minWidth: "70px" }}
        >
          {/* Delete Button */}
          <button
            onClick={() => deleteServiceClick(item)}
            className="btn btn-link text-danger p-0 mb-2"
            disabled={currentLoading}
          >
            <FaTrash size={16} />
          </button>

          {/* Quantity Picker */}
          <div className="bg-light rounded-pill px-3 py-1 mb-2">
            <button
              onClick={() => showQuantityDropdown(item)}
              className="btn btn-outline-primary btn-sm d-flex align-items-center"
              disabled={currentLoading}
            >
              <span className="fw-bold">{item.Qty}</span>
              <FaChevronDown size={12} className="ms-1" />
            </button>
          </div>
          
          {/* Item Total */}
          {item.TotalAmount && (
            <small className="fw-bold text-dark">
              Item Total: ₹{item.TotalAmount}
            </small>
          )}
        </div>
      </div>
    </div>
  );

  const EmptyCartState = () => (
    <div className="text-center py-5">
      <div className="mb-4">
        <div className="bg-light rounded-circle d-inline-flex p-4">
          {activeTab === "shop" ? (
            <FaShoppingCart size={48} className="text-muted" />
          ) : (
            <FaScissors size={48} className="text-muted" />
          )}
        </div>
      </div>
      <h4 className="fw-bold text-dark mb-3">
        Your {activeTab === "shop" ? "Shop" : "Salon"} Cart is Empty!
      </h4>
      <p className="text-muted mb-4">
        {activeTab === "shop"
          ? "You have no shop items added in the cart."
          : "You have no salon services added in the cart."}
      </p>
      <button
        className="btn btn-primary px-4 py-2 fw-bold rounded-pill"
        onClick={() => navigate("/")}
      >
        EXPLORE {activeTab === "shop" ? "SHOP" : "SALON"} SERVICES
      </button>
    </div>
  );

  const LoginPrompt = () => (
    <div className="text-center py-5">
      <div className="mb-4">
        <div className="bg-light rounded-circle d-inline-flex p-4">
          <FaShoppingCart size={48} className="text-muted" />
        </div>
      </div>
      <h4 className="fw-bold text-dark mb-3">Login Required!</h4>
      <p className="text-muted mb-4">
        Please login to view and manage your cart
      </p>
      <button
        className="btn btn-primary px-4 py-2 fw-bold rounded-pill"
        onClick={() => navigate("/login")}
      >
        LOGIN TO CONTINUE
      </button>
    </div>
  );

  const CartSummary = () => {
    return (
      <div className="p-4 rounded-4" style={{ backgroundColor: "#fff" }}>
        {/* Heading */}
        <h5 className="fw-bold mb-4">Order Summary</h5>

        {/* Price Breakdown */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">
              Subtotal ({currentItems.length} items)
            </span>
            <span className="fw-semibold">₹{currentCartTotalMrp}</span>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Discount</span>
            <span className="text-success fw-semibold">-₹{savings}</span>
          </div>

          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Delivery</span>
            <span className="text-success fw-semibold">FREE</span>
          </div>

          <hr />

          <div className="d-flex justify-content-between align-items-center mb-3">
            <span className="fw-bold">Total Amount</span>
            <span className="fw-bold text-primary fs-5">
              ₹{currentNetPayable}
            </span>
          </div>
        </div>

        {/* Savings Message */}
        {savings > 0 && (
          <div className="bg-success bg-opacity-10 rounded-3 p-2 mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <small className="fw-semibold text-success">You Save</small>
              <small className="fw-bold text-success">₹{savings}</small>
            </div>
          </div>
        )}

        {/* Address Section */}
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">
              {activeTab === "shop" ? "Delivery Address" : "Service Address"}
            </span>
            {currentAddress ? (
              <span className="text-success fw-semibold small">✓ Selected</span>
            ) : (
              <span className="text-warning fw-semibold small">
                Not selected
              </span>
            )}
          </div>
          {currentAddress && (
            <div className="bg-light rounded-3 p-2 small">
              <div className="text-muted">
                {currentAddress.AName}, {currentAddress.City}
              </div>
            </div>
          )}
        </div>

        {/* Schedule Address & Proceed Button */}
        <button
          className={`btn w-100 py-3 fw-semibold rounded ${
            currentLoading || !isAuthenticated || currentItems.length === 0
              ? "btn-secondary disabled"
              : "btn-primary"
          }`}
          onClick={handleScheduleAddress}
          disabled={currentLoading || !isAuthenticated || currentItems.length === 0}
          style={{ backgroundColor: "#b93c5b", border: "none" }}
        >
          {currentLoading ? (
            <>
              <div
                className="spinner-border spinner-border-sm me-2"
                role="status"
              ></div>
              PROCESSING...
            </>
          ) : (
            <>
              {activeTab === "shop" ? "SELECT SCHEDULE & ADDRESS" : "SELECT SLOT"}
              <FaArrowRight className="ms-2" />
            </>
          )}
        </button>
      </div>
    );
  };

  const TabHeader = () => (
    <div className="bg-white">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="nav nav-pills nav-justified gap-2 p-3">
              <button
                className={`nav-link py-3 fw-bold ${
                  activeTab === "shop"
                    ? "active text-white"
                    : "text-dark bg-light"
                }`}
                style={{
                  backgroundColor: activeTab === "shop" ? "#1469A7" : "",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                }}
                onClick={() => setActiveTab("shop")}
              >
                <FaShoppingCart className="me-2" />
                Shop Orders ({shopItems.length})
              </button>
              <button
                className={`nav-link py-3 fw-bold ${
                  activeTab === "salon"
                    ? "active text-white"
                    : "text-dark bg-light"
                }`}
                style={{
                  backgroundColor: activeTab === "salon" ? "#aa2b57" : "",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                }}
                onClick={() => setActiveTab("salon")}
              >
                <FaScissors className="me-2" />
                Salon Orders ({salonItems.length})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  if (!isAuthenticated) {
    return (
      <div className="min-vh-100 bg-light">
        {/* Header */}
        <div className="bg-primary text-white py-4 position-sticky top-0 z-3">
          <div className="container-fluid">
            <div className="row align-items-center">
              <div className="col-auto">
                <button
                  onClick={() => navigate(-1)}
                  className="btn btn-link text-white p-0"
                >
                  <FaChevronLeft size={20} />
                </button>
              </div>
              <div className="col text-center">
                <h5 className="mb-0 fw-bold">My Cart</h5>
              </div>
              <div className="col-auto" style={{ width: "40px" }}></div>
            </div>
          </div>
        </div>

        {/* Login Prompt Content */}
        <div className="container-fluid">
          <LoginPrompt />
        </div>
      </div>
    );
  }

  const hasCurrentItems = currentItems.length > 0;

  return (
    <div className="min-vh-100 bg-light container">
      {/* Header */}
      <div
        className="text-white py-4 position-sticky top-0 z-3"
        style={{ backgroundColor: "#f5f6fb" }}
      >
        <div className="container-fluid">
          <div className="row align-items-center">
            <div className="col-auto">
              <button
                onClick={() => navigate(-1)}
                className="btn btn-link text-white p-0"
                style={{ color: "#000" }}
              >
                <FaChevronLeft size={20} />
              </button>
            </div>
            <div className="col text-center">
              <h5 className="mb-0 fw-bold" style={{ color: "#000" }}>
                My Cart
              </h5>
            </div>
            <div className="col-auto">
              {hasCurrentItems && (
                <button
                  onClick={handleClearCart}
                  className="btn btn-link p-0"
                  title="Clear Cart"
                  style={{ color: "#000" }}
                >
                  <FaTimes size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <TabHeader />

      {/* Main Content */}
      <div className="container-fluid py-4">
        <div className="row">
          {/* Left Side - Cart Items */}
          <div className="col-lg-8 col-md-7 mb-4">
            {hasCurrentItems ? (
              <div>
                {currentItems.map((item, index) => (
                  <CartItemCard key={`${activeTab}-${item.ProductId || index}`} item={item} />
                ))}
              </div>
            ) : (
              <EmptyCartState />
            )}
          </div>

          {/* Right Side - Cart Summary */}
          <div className="col-lg-4 col-md-5">
            <CartSummary />
          </div>
        </div>
      </div>

      {/* Quantity Dropdown Modal */}
      {dropdownVisible && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          onClick={() => setDropdownVisible(false)}
        >
          <div
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content rounded-3 w-50">
              <div className="modal-header border-bottom-0 pb-0">
                <h6 className="modal-title fw-bold text-dark">
                  Select Quantity
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDropdownVisible(false)}
                ></button>
              </div>
              <div className="modal-body text-center py-3">
                <p className="text-muted mb-3 fs-6">
                  {selectedItem?.ProductName}
                </p>
                <div
                  className="list-group border-0"
                  style={{ maxHeight: "250px", overflowY: "auto" }}
                >
                  {quantities.map((quantity) => (
                    <button
                      key={quantity}
                      className={`list-group-item list-group-item-action border-0 rounded-2 mb-1 d-flex justify-content-between align-items-center py-2 ${
                        selectedItem?.Qty === quantity
                          ? "bg-primary text-white"
                          : "bg-light text-dark"
                      }`}
                      onClick={() => updateQuantity(quantity)}
                    >
                      <span className="fw-bold">{quantity}</span>
                      {selectedItem?.Qty === quantity && (
                        <FaCheck className="text-white" size={14} />
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="modal-footer border-top-0">
                <button
                  type="button"
                  className="btn btn-outline-secondary w-100 rounded-pill py-2"
                  onClick={() => setDropdownVisible(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopCart;